/**
 * Authentication Routes
 * Public routes for login, password reset, etc.
 */

import React, { lazy, Suspense } from "react";
import { RouteObject, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAuthStore } from "@/store/authStore";
import { LazyPage } from "../helpers";

// Auth Pages - Load immediately (critical path)
import { LoginPage } from "@/pages/auth/LoginPage";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
import { UnauthorizedPage } from "@/pages/auth/UnauthorizedPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import { homePathForRole } from "@/utils/homePath";

// Public pages
const PublicApplicationFormPage = lazy(() => import("@/pages/applications/PublicApplicationFormPage"));
const PlatformInvoicePaymentPage = lazy(() => import("@/pages/public/PlatformInvoicePaymentPage"));
const PublicCampaignDonatePage = lazy(() => import("@/pages/public/PublicCampaignDonatePage"));
const PublicPotluckSignupPage = lazy(() => import("@/pages/public/PublicPotluckSignupPage"));
const PaymentCompletePage = lazy(() => import("@/pages/payments/PaymentCompletePage"));
const LandingPage = lazy(() => import("@/pages/landing/LandingPage"));
// Unlisted component gallery — verification surface for design-system work.
const StyleGuidePage = lazy(() => import("@/pages/StyleGuidePage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/legal/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/legal/TermsOfServicePage"));

/**
 * Role-based redirect component - redirects users to appropriate dashboard
 */
export const RoleBasedRedirect: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={homePathForRole(user.role)} replace />;
};

/**
 * Root route wrapper - shows landing page for visitors, redirects authenticated users
 */
const LandingOrDashboard: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  if (isAuthenticated) return <RoleBasedRedirect />;

  // Only show landing page on the main marketing domain
  const hostname = window.location.hostname;
  const isMarketingDomain =
    hostname === "ilmiq.com" ||
    hostname === "www.ilmiq.com" ||
    hostname === "localhost";

  if (!isMarketingDomain) return <Navigate to="/login" replace />;

  return (
    <Suspense fallback={
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    }>
      <LandingPage />
    </Suspense>
  );
};

export const authRoutes: RouteObject[] = [
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "/unauthorized",
    element: <UnauthorizedPage />,
  },
  {
    path: "/apply",
    element: <LazyPage><PublicApplicationFormPage /></LazyPage>,
  },
  {
    // Tokenized payment link from a tenant's IlmIQ platform invoice email
    path: "/pay/platform-invoice/:invoiceId",
    element: <LazyPage><PlatformInvoicePaymentPage /></LazyPage>,
  },
  {
    path: "/payment-complete",
    element: <LazyPage><PaymentCompletePage /></LazyPage>,
  },
  {
    // Public no-login fundraising sponsor page, reached via a campaign's
    // shared link (token IS the credential).
    path: "/sponsor/:token",
    element: <LazyPage><PublicCampaignDonatePage /></LazyPage>,
  },
  {
    // Public no-login potluck / volunteer sign-up sheet, reached via a
    // potluck's shared link (token IS the credential).
    path: "/potluck/:token",
    element: <LazyPage><PublicPotluckSignupPage /></LazyPage>,
  },
  {
    // Unlisted design-system gallery (public, data-free) for visual verification.
    path: "/styleguide",
    element: <LazyPage><StyleGuidePage /></LazyPage>,
  },
  {
    path: "/privacy",
    element: <LazyPage><PrivacyPolicyPage /></LazyPage>,
  },
  {
    path: "/terms",
    element: <LazyPage><TermsOfServicePage /></LazyPage>,
  },
  {
    path: "/",
    element: <LandingOrDashboard />,
  },
];
